import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { ChevronDown, Check, Search } from "lucide-react";

// Shared, polished form primitives for GridForge's dense floating panels & editors.
// Goal: consistent, premium inputs/selects/labels with clear focus states — no more
// thin "thrown-together" fields. Canonical class recipes live here so every panel matches.

export const FIELD_INPUT =
  "w-full text-sm bg-white border border-slate-200 rounded-md px-3 py-2 font-medium text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-[#003E87] focus:ring-2 focus:ring-[#003E87]/20 outline-none transition-all";

// Native <select> styled to match (custom chevron, no default OS arrow).
export const FIELD_SELECT =
  "w-full text-sm bg-white border border-slate-200 rounded-md pl-3 pr-9 py-2 font-medium text-slate-900 shadow-sm appearance-none bg-no-repeat focus:border-[#003E87] focus:ring-2 focus:ring-[#003E87]/20 outline-none transition-all " +
  "bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2214%22 height=%2214%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2364748b%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-[length:14px] bg-[right_0.6rem_center]";

export function FieldLabel({ children, hint, className }) {
  return (
    <div className={cn("text-xs font-medium text-slate-700 mb-1 flex items-center gap-1.5", className)}>
      <span>{children}</span>
      {hint && <span className="font-normal text-slate-400 normal-case tracking-normal">{hint}</span>}
    </div>
  );
}

export function SectionLabel({ children, icon: Icon, className, right }) {
  return (
    <div className={cn("flex items-center justify-between mb-2", className)}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#003E87]" />}{children}
      </div>
      {right}
    </div>
  );
}

// Text / number field with optional adornments ($ prefix, unit suffix).
export function TextField({ value, onChange, prefix, suffix, className, testid, ...rest }) {
  return (
    <div className={cn("relative flex items-center rounded-md border border-slate-200 bg-white shadow-sm focus-within:border-[#003E87] focus-within:ring-2 focus-within:ring-[#003E87]/20 transition-all", className)}>
      {prefix && <span className="pl-3 text-slate-400 text-sm select-none shrink-0">{prefix}</span>}
      <input value={value} onChange={onChange} data-testid={testid}
        className={cn("flex-1 min-w-0 bg-transparent py-2 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none", prefix ? "pl-1.5" : "pl-3", suffix ? "pr-1.5" : "pr-3")} {...rest} />
      {suffix && <span className="pr-3 text-slate-400 text-xs font-medium select-none shrink-0">{suffix}</span>}
    </div>
  );
}

// Smart combobox: free text entry + a filtered dropdown of values already in use.
// Dropdown is portaled & position-fixed so it never clips inside scrolling panels.
export function Combobox({ value, onChange, options = [], placeholder, testid, className, disabled, icon: Icon = Search }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const reposition = () => { if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect()); };
    reposition();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("resize", reposition); window.removeEventListener("scroll", reposition, true); };
  }, [open]);

  const opts = useMemo(() => {
    const arr = (options || []).map((o) => (o && typeof o === "object") ? { value: String(o.value), label: String(o.label ?? o.value) } : { value: String(o), label: String(o) });
    const seen = new Set();
    return arr.filter((o) => o.value && !seen.has(o.value) && seen.add(o.value)).sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);
  const filter = (open ? q : "").toLowerCase();
  const shown = opts.filter((o) => o.label.toLowerCase().includes(filter) || o.value.toLowerCase().includes(filter));
  const current = opts.find((o) => o.value === (value || ""));
  const display = open ? q : (current ? current.label : (value || ""));
  const pick = (o) => { onChange(o.value); setQ(""); setOpen(false); };

  return (
    <div className={cn("relative", className)} ref={wrapRef}>
      <div className={cn("relative flex items-center rounded-md border border-slate-200 bg-white shadow-sm focus-within:border-[#003E87] focus-within:ring-2 focus-within:ring-[#003E87]/20 transition-all", disabled && "opacity-50 pointer-events-none bg-slate-50")}>
        <input
          value={display}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => { setQ(value || ""); setOpen(true); }}
          placeholder={placeholder}
          data-testid={testid}
          className="flex-1 min-w-0 bg-transparent pl-3 pr-1 py-2 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none truncate" />
        <button type="button" tabIndex={-1} disabled={disabled} onClick={() => { setOpen((o) => !o); setQ(value || ""); }} className="px-2 text-slate-400 hover:text-slate-600 shrink-0" data-testid={testid ? `${testid}-toggle` : undefined}>
          <ChevronDown className={cn("w-4 h-4 transition-transform", open && "rotate-180")} />
        </button>
      </div>
      {open && !disabled && rect && shown.length > 0 && createPortal(
        <div ref={popRef} style={{ position: "fixed", left: rect.left, top: rect.bottom + 4, width: rect.width, zIndex: 2147483646 }}
          className="max-h-52 overflow-auto rounded-md border border-slate-200 bg-white shadow-xl py-1" data-testid={testid ? `${testid}-list` : undefined}>
          {shown.map((o) => (
            <button key={o.value} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-[#003E87]/5 flex items-center justify-between gap-2"
              data-testid={testid ? `${testid}-opt-${o.value}` : undefined}>
              <span className="truncate flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5 text-slate-300" />}{o.label}</span>
              {(value || "") === o.value && <Check className="w-3.5 h-3.5 text-[#003E87] shrink-0" />}
            </button>
          ))}
        </div>,
        document.getElementById("takeoff-fs-root") || document.body
      )}
    </div>
  );
}
